import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  useMediaQuery,
  Avatar,
  Chip,
  Button,
  LinearProgress,
  CircularProgress,
  alpha,
  Divider,
  Stack,
} from "@mui/material";
import {
  useGetAllDhashboardFristQuery,
  useGetTeamQuery,
  useGetPositionQuery,
  useGetClassScheduleQuery,
  useGetAdminAlertsTodayQuery,
} from "../../Api/attoDeskApi";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import { groupClassRows, colorForSubject, ClassScheduleRow } from "../ClassCalendar/classEvents";
import { ITeam, IPosition } from "../../Api/Interface/api.interface";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import useIsMobile from "../../Core/hook/useIsMobile";
import useUser from "../../Core/hook/useUser";
import { Privileges } from "../../Core/Enum/enum";
import { formatLateDuration } from "../../utils/lateDuration";
import { DashboardLoader } from "../../Components/Loader";
import OperationsCommandCenter from "./OperationsCommandCenter";
import FinanceOverview from "./FinanceOverview";
import DailyBriefingBar from "./DailyBriefingBar";
import { DashboardTintedCard } from "./DashboardTintedCard";
import { dashTokens, toneMap, Tone } from "./dashboardTheme";

// Icons
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PaymentIcon from "@mui/icons-material/Payment";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import ReceiptIcon from "@mui/icons-material/Receipt";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import MessageIcon from "@mui/icons-material/Message";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import GroupIcon from "@mui/icons-material/Group";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { formatFromPrejoined } from "../../utils/classLabel";

const capitalizeFirstLetter = (name: string | null | undefined) => {
  if (!name) return "";
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

// ─── AdminAlertsStrip (Batch 11) ────────────────────────────────────
// Renders today's class-session alerts (from admin_alerts). Three
// distinguished types: not_started, late_start, auto_completed_unmarked.
// Sorted most-recent first by the endpoint. Calm empty state.
type AlertRow = {
  id: number;
  staff_id: number | null;
  occurrence_id: number;
  alert_type: "not_started" | "late_start" | "auto_completed_unmarked" | string;
  payload: {
    staff_name?: string;
    grade?: string | null;
    scheduled_start?: string;
    actual_start?: string;
    scheduled_end?: string;
    actual_end?: string;
    scheduled_date?: string;
    late_minutes?: number;
    minutes_past_start?: number;
    unmarked_count?: number;
  };
  created_at: string;
};

const ALERT_TYPE_META: Record<string, { title: string; color: string; icon: string }> = {
  not_started: { title: "Class not started", color: "#dc2626", icon: "🚨" },
  late_start: { title: "Started late", color: "#f59e0b", icon: "⏰" },
  auto_completed_unmarked: { title: "Auto-completed with unmarked students", color: "#7c3aed", icon: "📋" },
};

const AdminAlertsStrip: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading } = useGetAdminAlertsTodayQuery(undefined, {
    // Fresh enough to feel live without hammering the API.
    pollingInterval: 60000,
  });
  const payload: any = data?.data;
  const rows: AlertRow[] = (payload?.rows || []) as AlertRow[];
  const count = payload?.count ?? rows.length;

  if (isLoading) return null;

  if (!rows.length) {
    return (
      <Box sx={{
        mb: 3, bgcolor: "#f0fdf4", border: "1px solid #bbf7d0",
        borderRadius: "14px", px: 2.5, py: 1.75,
        display: "flex", alignItems: "center", gap: 1.25,
      }}>
        <Box sx={{ fontSize: 18 }}>✅</Box>
        <Box>
          <Typography sx={{ fontWeight: 700, color: "#166534", fontSize: 13 }}>
            No class-session alerts today
          </Typography>
          <Typography sx={{ color: "#15803d", fontSize: 11 }}>
            Everything is running on time.
          </Typography>
        </Box>
      </Box>
    );
  }

  const formatTime = (iso?: string) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); }
    catch { return iso; }
  };

  const describe = (a: AlertRow): string => {
    if (a.alert_type === "not_started") {
      const m = a.payload.minutes_past_start ?? 0;
      return `${m} minute${m === 1 ? "" : "s"} past scheduled start with no start recorded.`;
    }
    if (a.alert_type === "late_start") {
      // Older admin_alerts rows may still carry a fractional
      // late_minutes value in payload — the formatter rounds it.
      return `Started ${formatLateDuration(a.payload.late_minutes)}.`;
    }
    if (a.alert_type === "auto_completed_unmarked") {
      const n = a.payload.unmarked_count ?? 0;
      return `Auto-completed with ${n} unmarked student${n === 1 ? "" : "s"}.`;
    }
    return "";
  };

  return (
    <Box sx={{
      mb: 3, bgcolor: "#fef2f2", border: "1px solid #fecaca",
      borderRadius: "14px", overflow: "hidden",
    }}>
      <Box sx={{
        px: 2.5, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: "1px solid #fecaca", bgcolor: "#fee2e2",
      }}>
        <Typography sx={{ fontWeight: 800, color: "#991b1b", fontSize: 13, display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberIcon sx={{ fontSize: 18 }} />
          Today's alerts
          <Box component="span" sx={{ ml: 0.5, bgcolor: "#991b1b", color: "#fff", borderRadius: "999px", px: 1, py: 0.1, fontSize: 11, fontWeight: 700 }}>
            {count}
          </Box>
        </Typography>
      </Box>
      <Stack divider={<Divider />}>
        {rows.map((a) => {
          const meta = ALERT_TYPE_META[a.alert_type] || { title: a.alert_type, color: "#64748b", icon: "•" };
          const schedTime = formatTime(a.payload.scheduled_start || a.payload.scheduled_end);
          const actualTime = formatTime(a.payload.actual_start || a.payload.actual_end);
          return (
            <Box
              key={a.id}
              onClick={() => navigate(`/class-sessions/${a.occurrence_id}/attendance?return_url=/dashboard`)}
              sx={{
                px: 2.5, py: 1.5, display: "flex", alignItems: "center", gap: 2,
                cursor: "pointer", "&:hover": { bgcolor: "#fff5f5" },
                borderLeft: `4px solid ${meta.color}`,
              }}
            >
              <Box sx={{
                fontSize: 20, width: 32, height: 32, borderRadius: "8px",
                bgcolor: alpha(meta.color, 0.12), color: meta.color,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>{meta.icon}</Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
                  <Typography sx={{ fontWeight: 800, color: "#1a1a2e", fontSize: 14 }}>
                    {a.payload.staff_name || "Staff"}
                  </Typography>
                  {a.payload.grade && (
                    <Typography sx={{ color: "#64748b", fontSize: 12 }}>
                      · {a.payload.grade}
                    </Typography>
                  )}
                  <Chip label={meta.title} size="small" sx={{
                    ml: 0.5, height: 20, fontSize: 10, fontWeight: 700,
                    bgcolor: alpha(meta.color, 0.12), color: meta.color, border: `1px solid ${alpha(meta.color, 0.3)}`,
                  }} />
                </Box>
                <Typography sx={{ color: "#475569", fontSize: 12, mt: 0.25 }}>
                  {describe(a)}
                  {(schedTime || actualTime) && (
                    <Box component="span" sx={{ color: "#94a3b8", ml: 1 }}>
                      {schedTime && <>Scheduled {schedTime}</>}
                      {schedTime && actualTime && " · "}
                      {actualTime && <>Actual {actualTime}</>}
                    </Box>
                  )}
                </Typography>
              </Box>
              <ArrowForwardIcon sx={{ color: "#94a3b8", fontSize: 18 }} />
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

const Index = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { userHasRight, staffId: authStaffId } = useUser();
  // Shared dashboard location filter (Aug 2026): one source of truth
  // consumed by both FinanceOverview and OperationsCommandCenter so
  // the two sections can never show different scopes at the same time.
  // Server still enforces per-endpoint permission + location scope —
  // this is a UX consistency wire, not a security boundary.
  const [dashboardLocationId, setDashboardLocationId] = useState<number | "all">("all");
  const canSeeFinance = userHasRight(Privileges.PaymentView) || userHasRight(Privileges.InvoiceView);
  const canSeeStaffAdmin = userHasRight(Privileges.StaffView);
  const isLimitedStaff = !canSeeStaffAdmin && !!authStaffId;

  const { data: clientData, isLoading: clientIsLoading, isError: clientIsError } = useGetAllDhashboardFristQuery({ id: "" });
  const { data: tenantData, isLoading: tenantIsLoading, isError: tenantIsError } = useGetTeamQuery({ searchText: "", id: "" });
  const { data: positionData, isLoading: positionIsLoading, isError: positionIsError } = useGetPositionQuery({ searchText: "" });

  const teamList = useMemo(() => (tenantData?.data ?? []) as ITeam[], [tenantData?.data]);
  const positionList = useMemo(() => (positionData?.data ?? []) as IPosition[], [positionData?.data]);
  const postionMap = useMemo(() => { const m = new Map(); positionList?.forEach((p) => m.set(p.id, p.positionName)); return m; }, [positionList]);
  const teamMap = useMemo(() => {
    const m = new Map<number, { name: string; image: string; positionName: string | null }>();
    teamList?.forEach((t) => {
      m.set(t.id, { name: `${capitalizeFirstLetter(t.teamFirstName)} ${capitalizeFirstLetter(t.teamLastName)}`.trim(), image: t.teamImg, positionName: postionMap.get(t.teamPositionId) || null });
    });
    return m;
  }, [teamList, postionMap]);

  // ── Class Calendar widget: same generated class-instances feed as the Class Calendar page ──
  const monthStart = useMemo(() => dayjs().startOf("month"), []);
  const monthEnd = useMemo(() => dayjs().endOf("month"), []);
  const showClassCalendar = userHasRight(Privileges.ClassScheduleView);
  const { data: classScheduleData, isLoading: classScheduleIsLoading, isError: classScheduleIsError } = useGetClassScheduleQuery(
    {
      searchText: "",
      startDate: monthStart.format("YYYY-MM-DD"),
      endDate: monthEnd.format("YYYY-MM-DD"),
      staffId: "",
      studentId: "",
      status: "",
      // Shared dashboard location filter (Aug 2026 final polish). The
      // /class-schedule/getAll endpoint already accepts `location_id`
      // and applies it via applyPhase2Filters, so this brings the
      // calendar under the same source of truth as Finance + Operations
      // without any backend change.
      location_id: dashboardLocationId === "all" ? undefined : dashboardLocationId,
    },
    { skip: !showClassCalendar }
  );
  const classCalendarEvents = useMemo(() => {
    const rows = Array.isArray(classScheduleData?.data) ? (classScheduleData?.data as ClassScheduleRow[]) : [];
    return groupClassRows(rows).map((ev) => ({
      id: ev.key,
      title: `${ev.subjectName || "Class"}${ev.studentCount ? ` (${ev.studentCount})` : ""}`,
      start: `${ev.scheduledDate}T${ev.startTime}`,
      end: `${ev.scheduledDate}T${ev.endTime}`,
      backgroundColor: colorForSubject(ev.subjectName),
      borderColor: colorForSubject(ev.subjectName),
    }));
  }, [classScheduleData]);

  // Data
  const attendanceRate = clientData?.data?.attendanceRate ?? 0;
  const presentCount = clientData?.data?.presentCount ?? 0;
  const absentCount = clientData?.data?.absentCount ?? 0;
  const lateCount = clientData?.data?.lateCount ?? 0;
  const allUpcomingClassesToday = clientData?.data?.upcomingClassesToday ?? [];
  const upcomingClassesToday = isLimitedStaff
    ? allUpcomingClassesToday.filter((cls: any) => cls.staffId === authStaffId || cls.staff_id === authStaffId)
    : allUpcomingClassesToday;
  const recentPayments = clientData?.data?.recentPayments ?? [];
  const scheduledCount = isLimitedStaff ? upcomingClassesToday.length : (clientData?.data?.todaySchedules ?? 0);
  const completedCount = isLimitedStaff
    ? upcomingClassesToday.filter((c: any) => c.status === "completed").length
    : (clientData?.data?.completedToday ?? 0);
  const totalStudentsGlobal = clientData?.data?.totalStudent ?? 0;
  const totalStaff = clientData?.data?.employee_count ?? 0;
  const overdueInvoices = clientData?.data?.overdueInvoices ?? 0;
  const monthlyRevenue = clientData?.data?.monthlyRevenue ?? 0;

  // For limited staff: count unique students from their classes only
  const myStudentCount = useMemo(() => {
    if (!isLimitedStaff) return totalStudentsGlobal;
    const ids = new Set<number>();
    upcomingClassesToday.forEach((cls: any) => {
      if (cls.studentCount) ids.add(cls.studentCount); // fallback to count
      if (Array.isArray(cls.students)) cls.students.forEach((s: any) => ids.add(s.id || s.studentId));
    });
    return ids.size > 0 ? ids.size : upcomingClassesToday.reduce((sum: number, c: any) => sum + (c.studentCount || 0), 0);
  }, [isLimitedStaff, upcomingClassesToday, totalStudentsGlobal]);
  const totalStudents = isLimitedStaff ? myStudentCount : totalStudentsGlobal;

  // Staff name
  const staffInfo = authStaffId ? teamMap.get(authStaffId) : null;
  const greeting = (() => {
    const h = dayjs().hour();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  })();

  // Next class
  const nextClass = useMemo(() => {
    const now = dayjs();
    return upcomingClassesToday.find((cls: any) => {
      if (cls.status === "completed" || cls.status === "cancelled") return false;
      if (!cls.startTime) return false;
      const [h, m] = cls.startTime.split(":").map(Number);
      return now.isBefore(dayjs().hour(h).minute(m));
    }) || null;
  }, [upcomingClassesToday]);

  // Time status helper
  const getTimeStatus = (cls: any) => {
    const now = dayjs();
    if (cls.status === "completed") return { label: "Completed", color: "#22c55e" };
    if (cls.status === "cancelled") return { label: "Cancelled", color: "#ef4444" };
    if (!cls.startTime || !cls.endTime) return { label: "Scheduled", color: "#6366f1" };
    const [sh, sm] = cls.startTime.split(":").map(Number);
    const [eh, em] = cls.endTime.split(":").map(Number);
    const start = dayjs().hour(sh).minute(sm).second(0);
    const end = dayjs().hour(eh).minute(em).second(0);
    if (now.isBefore(start)) {
      const diff = start.diff(now, "minute");
      const hh = Math.floor(diff / 60);
      return { label: hh > 0 ? `In ${hh}h ${diff % 60}m` : `In ${diff}m`, color: "#6366f1" };
    }
    if (now.isBefore(end)) return { label: "Live now", color: "#f59e0b" };
    return { label: "Completed", color: "#22c55e" };
  };

  // Combine every request the initial dashboard render depends on. Any
  // in-flight request keeps the branded loader up so zero-value KPIs,
  // "No classes today" and empty attendance cards never flash before
  // the real payload arrives. A failure on any of them releases the
  // gate so the page can surface its own error / empty states rather
  // than leaving the loader stuck forever.
  const anyLoading =
    clientIsLoading ||
    tenantIsLoading ||
    positionIsLoading ||
    (showClassCalendar && classScheduleIsLoading);
  const anyError =
    clientIsError || tenantIsError || positionIsError || classScheduleIsError;
  const dashboardIsLoading = anyLoading && !anyError;

  // Keep the loader mounted through a short fade-out after data is
  // ready. StrictMode's double-invoke re-runs this effect but the ref
  // guard means the fade only fires once per real load cycle.
  const [showLoader, setShowLoader] = useState(true);
  const hasFadedOnceRef = useRef(false);
  useEffect(() => {
    if (!dashboardIsLoading && !hasFadedOnceRef.current) {
      hasFadedOnceRef.current = true;
      setShowLoader(false);
    }
  }, [dashboardIsLoading]);


  // Muted steel/indigo family only — no random per-subject rainbow.
  const subjectColors = ["#4E78A6", "#6F96BE", "#6878A6", "#8DA4B8", "#5D7FB0", "#6273A5", "#557AA1"];

  return (
    <>
    <DashboardLoader visible={showLoader} />
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#F4F7FA", minHeight: "100vh", mx: -3, mt: -3 }}>
      {/* ══════════ DAILY BRIEFING BAR (Aug 2026 executive command center) ══════════
          Rendered above everything else so an admin opening the dashboard in the
          morning sees, in the FIRST viewport:
            - Morning Brief (greeting + mini metric pills)
            - Top Priorities (ranked action list)
            - Today's Progress (classes / attendance / collections)
          Composes from the same DashboardFinance + DashboardOperations DTOs
          the sections below already fetch — RTK Query dedupes so no
          additional network calls. Auto-refresh indicator + manual refresh
          button included; empty-good states render positive messaging. */}
      <DailyBriefingBar
        locationId={dashboardLocationId}
        firstName={staffInfo?.name?.split(" ")[0]}
      />

      {/* ══════════ FINANCE OVERVIEW (Aug 2026 client request) ══════════
          Money first — Billed Today, Paid Today, Invoiced This Month,
          Outstanding, Collection Rate, plus Revenue by Location,
          Recent Payments, and Outstanding Invoices. Owns the single
          visible location selector for the whole dashboard. Server-
          gated by Invoice_View + System_Admin; component renders null
          on 403 so staff without finance permission never see a
          financial value. */}
      <FinanceOverview
        locationId={dashboardLocationId}
        onLocationChange={setDashboardLocationId}
      />

      {/* ══════════ OPERATIONS COMMAND CENTER (Aug 2026) ══════════
          Premium hero + read-only location pill + KPIs + red late-
          alert panel + Live Now + Starting Soon. Server-side scoped
          by user's location authorization. Uses the same shared
          location state as FinanceOverview above. */}
      <OperationsCommandCenter
        firstName={staffInfo?.name?.split(" ")[0]}
        locationId={dashboardLocationId}
        onLocationChange={setDashboardLocationId}
      />

      {/* Legacy duplicates removed (Aug 2026): Next Class Highlight,
          AdminAlertsStrip, KPI Cards, and Today's Schedule are now
          covered by the OperationsCommandCenter above. Quick Actions,
          Class Calendar, Attendance Summary, Insights, and Recent
          Payments follow below inside a simplified two-column layout. */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 340px" }, gap: 3 }}>
        <Stack spacing={3}>
          {/* ── Quick Actions (Aug 2026 colorful redesign) ──
              Full tinted action cards matching the dashboard palette.
              Existing RBAC and routes are preserved; only the visual
              treatment changed. */}
          <Box>
            <Typography sx={{
              fontSize: 11, fontWeight: 700, color: dashTokens.color.muted,
              textTransform: "uppercase", letterSpacing: "0.1em", mb: 0.5,
            }}>
              Shortcuts
            </Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: dashTokens.color.text, mb: 2, letterSpacing: "-0.01em" }}>
              Quick Actions
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: `repeat(${Math.min((canSeeStaffAdmin ? 1 : 0) + (canSeeFinance ? 1 : 0) + 3, 5)}, 1fr)` }, gap: 2 }}>
              {([
                { label: "Mark Attendance", desc: "Record today's classes",  icon: <FactCheckIcon />,     tone: "green"  as Tone, path: "/attendance-record/list" },
                { label: "Class Schedule",  desc: "View all sessions",       icon: <CalendarMonthIcon />, tone: "blue"   as Tone, path: "/class-schedule/list" },
                { label: "Send Message",    desc: "Contact parents",          icon: <MessageIcon />,       tone: "cyan"   as Tone, path: "/messages" },
                ...(canSeeStaffAdmin ? [{ label: "Add Student", desc: "New enrollment", icon: <PersonAddAltIcon />, tone: "purple" as Tone, path: "/student/create" }] : []),
                ...(canSeeFinance    ? [{ label: "Invoices",    desc: "Billing & payments", icon: <ReceiptIcon />, tone: "amber" as Tone, path: "/invoice/list" }] : []),
              ]).map((act) => (
                <DashboardTintedCard
                  key={act.label} tone={act.tone} interactive
                  onClick={() => navigate(act.path)}
                  padding={2.25}
                  icon={act.icon}
                  label={act.label}
                  hint={act.desc}
                />
              ))}
            </Box>
          </Box>

          {/* ── Class Calendar ──
              Clean white surface with a blue-tinted border and an
              indigo header icon (spec §"CALENDAR" — calendar itself
              stays predominantly white for readability, only the
              header/icon carries the indigo/blue accent). Honors the
              shared dashboardLocationId so Finance / Operations /
              Calendar always show the same scope. Backend endpoint
              already supports `location_id` — no API changes. */}
          {showClassCalendar && (
            <Box sx={{
              borderRadius: `${dashTokens.radius.lg}px`,
              border: `1px solid ${toneMap.indigo.border}`,
              boxShadow: dashTokens.shadow.card,
              // Spec §17 — outer calendar card carries a soft indigo
              // full fill; the actual calendar grid inside stays
              // light/neutral for readability (untouched).
              bgcolor: toneMap.indigo.grad,
              p: { xs: 2, md: 2.5 },
            }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.75, gap: 1.5, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <Box sx={{
                    width: 34, height: 34, borderRadius: `${dashTokens.radius.sm}px`,
                    bgcolor: toneMap.indigo.iconBg, color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: `0 4px 12px ${toneMap.indigo.iconBg}40`,
                    "& svg": { fontSize: 18 },
                  }}>
                    <CalendarMonthIcon />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 15, fontWeight: 800, color: dashTokens.color.text, lineHeight: 1.1 }}>
                      Class Calendar
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: dashTokens.color.muted, mt: 0.25 }}>
                      Schedule for {dashboardLocationId === "all" ? "all authorized locations" : "the selected location"}
                    </Typography>
                  </Box>
                </Box>
                <Button size="small" variant="outlined" endIcon={<ArrowForwardIcon sx={{ fontSize: "15px !important" }} />}
                  onClick={() => navigate("/class-schedule/list")}
                  sx={{
                    textTransform: "none", fontSize: 12, fontWeight: 700,
                    borderRadius: "10px", height: 32, px: 1.5,
                    color: toneMap.purple.accent,
                    borderColor: `${toneMap.purple.iconBg}55`,
                    bgcolor: "#FFFFFFAA",
                    "&:hover": { borderColor: toneMap.purple.iconBg, bgcolor: "#FFFFFF" },
                  }}>
                  Open full calendar
                </Button>
              </Box>
              <Box sx={{
                bgcolor: "#FFFFFFCC", borderRadius: `${dashTokens.radius.md}px`,
                border: `1px solid ${toneMap.blue.border}`,
                p: 1.5,
                "& .fc": { fontFamily: "inherit", fontSize: 11 },
                "& .fc-toolbar-title": { fontSize: "15px !important", fontWeight: 800, color: dashTokens.color.text },
                "& .fc-button": {
                  textTransform: "capitalize", fontSize: "11px !important",
                  padding: "3px 10px !important",
                  border: `1px solid ${toneMap.blue.border} !important`,
                  backgroundColor: "#FFFFFF !important",
                  color: `${toneMap.blue.accent} !important`,
                  boxShadow: "none !important",
                  borderRadius: "8px !important",
                },
                "& .fc-button:hover": { backgroundColor: `${toneMap.blue.grad} !important` },
                "& .fc-button-active": { backgroundColor: `${toneMap.blue.iconBg} !important`, color: "#fff !important", borderColor: `${toneMap.blue.iconBg} !important` },
                "& .fc-col-header-cell": { fontWeight: "700 !important", fontSize: "10.5px !important", color: `${dashTokens.color.muted} !important`, textTransform: "uppercase", letterSpacing: "0.06em" },
                "& .fc-daygrid-event": { cursor: "pointer", borderRadius: "6px", fontSize: "10px", padding: "1px 4px", fontWeight: 600 },
                "& .fc-day-today": { backgroundColor: `${toneMap.blue.grad} !important` },
                "& .fc-day-today .fc-daygrid-day-number": { fontWeight: "800 !important", color: `${toneMap.blue.accent} !important` },
                "& .fc-scrollgrid": { borderRadius: "10px", overflow: "hidden", border: `1px solid ${dashTokens.color.border} !important` },
                "& .fc-daygrid-more-link": {
                  backgroundColor: toneMap.purple.grad,
                  color: `${toneMap.purple.accent} !important`,
                  padding: "1px 6px !important",
                  borderRadius: "999px !important",
                  fontWeight: "700 !important",
                  fontSize: "9.5px !important",
                },
              }}>
                <FullCalendar
                  plugins={[dayGridPlugin]}
                  initialView="dayGridMonth"
                  headerToolbar={{ left: "prev,next", center: "title", right: "today" }}
                  height={420}
                  dayMaxEvents={2}
                  events={classCalendarEvents}
                  eventClick={(info) => { info.jsEvent.preventDefault(); navigate("/class-schedule/list"); }}
                />
              </Box>
            </Box>
          )}
        </Stack>

        {/* RIGHT SIDEBAR */}
        <Stack spacing={3}>
          {/* ── Today's Attendance (Aug 2026 green tint) ── */}
          <DashboardTintedCard tone="green" padding={2.5} bloom={false}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, gap: 1, flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: `${dashTokens.radius.sm}px`,
                  bgcolor: toneMap.green.iconBg, color: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: `0 4px 12px ${toneMap.green.iconBg}40`,
                  "& svg": { fontSize: 18 },
                }}>
                  <FactCheckIcon />
                </Box>
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: toneMap.green.accent }}>Today's Attendance</Typography>
              </Box>
              <Button size="small" variant="contained" onClick={() => navigate("/attendance-record/list")}
                sx={{
                  textTransform: "none", fontSize: 11.5, fontWeight: 700,
                  borderRadius: "10px", height: 30, px: 1.5,
                  bgcolor: toneMap.green.iconBg, boxShadow: "none",
                  "&:hover": { bgcolor: "#0F8437", boxShadow: `0 4px 12px ${toneMap.green.iconBg}55` },
                }}>
                Mark Attendance
              </Button>
            </Box>
            {presentCount === 0 && absentCount === 0 && lateCount === 0 ? (
              // Empty state (spec §5 — no misleading 0%).
              <Box sx={{ textAlign: "center", py: 2.5 }}>
                <Box sx={{
                  width: 52, height: 52, borderRadius: "14px",
                  background: toneMap.green.grad, color: toneMap.green.accent,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  mx: "auto", mb: 1.25,
                }}>
                  <FactCheckIcon sx={{ fontSize: 24 }} />
                </Box>
                <Typography sx={{ fontSize: 26, fontWeight: 800, color: toneMap.green.accent, lineHeight: 1 }}>—</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700, mt: 0.5, color: toneMap.green.accent }}>
                  No attendance recorded yet
                </Typography>
                <Typography sx={{ fontSize: 12, color: dashTokens.color.muted, mt: 0.5, maxWidth: 240, mx: "auto" }}>
                  {scheduledCount > 0 ? "Mark attendance for today's classes to track progress." : "No classes scheduled today."}
                </Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ textAlign: "center", mb: 2 }}>
                  <Box sx={{ position: "relative", display: "inline-flex" }}>
                    <CircularProgress variant="determinate" value={100} size={90} thickness={4} sx={{ color: "#FFFFFF88", position: "absolute" }} />
                    <CircularProgress variant="determinate" value={attendanceRate} size={90} thickness={4}
                      sx={{ color: attendanceRate > 80 ? toneMap.green.iconBg : attendanceRate > 60 ? toneMap.amber.iconBg : toneMap.red.iconBg, "& .MuiCircularProgress-circle": { strokeLinecap: "round" } }} />
                    <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                      <Typography sx={{ fontSize: 22, fontWeight: 800, color: toneMap.green.accent, lineHeight: 1 }}>{attendanceRate}%</Typography>
                      <Typography sx={{ fontSize: 8.5, fontWeight: 700, color: toneMap.green.accent, textTransform: "uppercase", letterSpacing: "0.06em", mt: 0.25 }}>Recorded</Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1 }}>
                  {[
                    { val: presentCount, label: "Present", color: toneMap.green.iconBg,  tint: "#FFFFFFCC" },
                    { val: lateCount,    label: "Late",    color: toneMap.amber.iconBg, tint: "#FFFFFFCC" },
                    { val: absentCount,  label: "Absent",  color: toneMap.red.iconBg,   tint: "#FFFFFFCC" },
                  ].map((s) => (
                    <Box key={s.label} sx={{
                      textAlign: "center", py: 1, borderRadius: "10px",
                      bgcolor: s.tint,
                      border: `1px solid ${s.color}30`,
                    }}>
                      <Typography sx={{ fontSize: 18, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.val}</Typography>
                      <Typography sx={{ fontSize: 10, color: dashTokens.color.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", mt: 0.25 }}>{s.label}</Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </DashboardTintedCard>

          {/* ── Insights (Aug 2026 purple/blue tint) ──
              Reuses the SAME real data the previous version showed
              (scheduledCount / overdueInvoices / absentCount). No
              fake trends invented per spec §7. */}
          <DashboardTintedCard tone="purple" padding={2.5} bloom={false}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
              <Box sx={{
                width: 32, height: 32, borderRadius: `${dashTokens.radius.sm}px`,
                bgcolor: toneMap.purple.iconBg, color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: `0 4px 12px ${toneMap.purple.iconBg}40`,
                "& svg": { fontSize: 18 },
              }}>
                <WarningAmberIcon />
              </Box>
              <Typography sx={{ fontSize: 15, fontWeight: 800, color: toneMap.purple.accent }}>Insights</Typography>
            </Box>
            <Stack spacing={1}>
              {scheduledCount > 0 && (() => {
                const t = toneMap.blue;
                return (
                  <Box onClick={() => navigate("/class-schedule/list")}
                    sx={{
                      display: "flex", alignItems: "center", gap: 1.25, p: 1.25,
                      borderRadius: `${dashTokens.radius.sm}px`,
                      bgcolor: "#FFFFFFCC", border: `1px solid ${t.iconBg}22`,
                      cursor: "pointer", transition: dashTokens.transition,
                      "&:hover": { transform: "translateX(2px)", borderColor: `${t.iconBg}55`, boxShadow: `0 4px 12px ${t.iconBg}18` },
                    }}>
                    <Box sx={{ width: 30, height: 30, borderRadius: "10px", bgcolor: t.iconBg, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 15 }, flexShrink: 0 }}>
                      <CalendarTodayIcon />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: t.accent }}>
                        {scheduledCount} class{scheduledCount > 1 ? "es" : ""} today
                      </Typography>
                      <Typography sx={{ fontSize: 10.5, color: dashTokens.color.muted }}>
                        {completedCount > 0 ? `${completedCount} done · ${scheduledCount - completedCount} left` : "Ready to start"}
                      </Typography>
                    </Box>
                  </Box>
                );
              })()}
              {overdueInvoices > 0 && canSeeFinance && (() => {
                const t = toneMap.red;
                return (
                  <Box onClick={() => navigate("/invoice/list")}
                    sx={{
                      display: "flex", alignItems: "center", gap: 1.25, p: 1.25,
                      borderRadius: `${dashTokens.radius.sm}px`,
                      bgcolor: "#FFFFFFCC", border: `1px solid ${t.iconBg}30`,
                      cursor: "pointer", transition: dashTokens.transition,
                      "&:hover": { transform: "translateX(2px)", borderColor: `${t.iconBg}66`, boxShadow: `0 4px 12px ${t.iconBg}20` },
                    }}>
                    <Box sx={{ width: 30, height: 30, borderRadius: "10px", bgcolor: t.iconBg, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 15 }, flexShrink: 0 }}>
                      <WarningAmberIcon />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: t.accent }}>
                        {overdueInvoices} overdue invoice{overdueInvoices > 1 ? "s" : ""}
                      </Typography>
                      <Typography sx={{ fontSize: 10.5, color: dashTokens.color.muted }}>Requires attention</Typography>
                    </Box>
                  </Box>
                );
              })()}
              {absentCount > 0 && (() => {
                const t = toneMap.amber;
                return (
                  <Box onClick={() => navigate("/attendance-record/list")}
                    sx={{
                      display: "flex", alignItems: "center", gap: 1.25, p: 1.25,
                      borderRadius: `${dashTokens.radius.sm}px`,
                      bgcolor: "#FFFFFFCC", border: `1px solid ${t.iconBg}30`,
                      cursor: "pointer", transition: dashTokens.transition,
                      "&:hover": { transform: "translateX(2px)", borderColor: `${t.iconBg}66`, boxShadow: `0 4px 12px ${t.iconBg}20` },
                    }}>
                    <Box sx={{ width: 30, height: 30, borderRadius: "10px", bgcolor: t.iconBg, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 15 }, flexShrink: 0 }}>
                      <AccessTimeIcon />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: t.accent }}>
                        {absentCount} student{absentCount > 1 ? "s" : ""} absent
                      </Typography>
                      <Typography sx={{ fontSize: 10.5, color: dashTokens.color.muted }}>Consider follow-up</Typography>
                    </Box>
                  </Box>
                );
              })()}
              {scheduledCount === 0 && (overdueInvoices === 0 || !canSeeFinance) && absentCount === 0 && (() => {
                const t = toneMap.green;
                return (
                  <Box sx={{
                    display: "flex", alignItems: "center", gap: 1.25, p: 1.25,
                    borderRadius: `${dashTokens.radius.sm}px`,
                    bgcolor: "#FFFFFFCC", border: `1px solid ${t.iconBg}30`,
                  }}>
                    <Box sx={{ width: 30, height: 30, borderRadius: "10px", bgcolor: t.iconBg, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 15 }, flexShrink: 0 }}>
                      <CheckCircleOutlineIcon />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: t.accent }}>All clear</Typography>
                      <Typography sx={{ fontSize: 10.5, color: dashTokens.color.muted }}>No pending actions or issues</Typography>
                    </Box>
                  </Box>
                );
              })()}
            </Stack>
          </DashboardTintedCard>

          {/* ── Recent Payments (finance users only) ── */}
          {canSeeFinance && recentPayments.length > 0 && (
            <Box sx={{ bgcolor: "#fff", borderRadius: "16px", border: "1px solid rgba(0,0,0,0.05)", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.75, borderBottom: "1px solid rgba(0,0,0,0.04)" }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Recent Payments</Typography>
                <Button size="small" onClick={() => navigate("/finance")}
                  sx={{ textTransform: "none", fontSize: 11, color: "text.secondary" }}>View All</Button>
              </Box>
              {recentPayments.slice(0, 5).map((p: any, i: number) => (
                <Box key={p.id || i} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.25, borderBottom: i < Math.min(recentPayments.length, 5) - 1 ? "1px solid rgba(0,0,0,0.03)" : "none" }}>
                  <Box>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>{p.studentName || "Student"}</Typography>
                    <Typography sx={{ fontSize: 10, color: "text.disabled" }}>{p.paymentDate ? dayjs(p.paymentDate).format("MMM D") : ""}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#5BAA83" }}>+${Number(p.amount || 0).toFixed(2)}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Stack>
      </Box>
    </Box>
    </>
  );
};

export default Index;
